import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiArrowLeft, FiEdit3, FiFile, FiCheckCircle } from "react-icons/fi";
import homeworkService from "../../services/homeworkService";
import GradeModal from "../../components/common/GradeModal";

const statusBadge = {
    Pending: "badge-muted",
    Submitted: "badge-available",
    Late: "badge-maintenance",
    Graded: "badge-full", // reusing an existing "strong" badge color for emphasis
};

const extractData = (res) => res?.data?.data ?? res?.data ?? null;
const extractList = (res) => {
    const list = extractData(res);
    return Array.isArray(list) ? list : [];
};

const getStudentName = (student) =>
    student?.fullName || student?.name || student?.displayName || "Student";

const getStudentMeta = (student) =>
    student?.rollNo || student?.rollNumber || student?.enrollmentNo || "";

export default function SubmissionsList() {
    const { homeworkId } = useParams();
    const navigate = useNavigate();

    const [homework, setHomework] = useState(null);
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [gradeTarget, setGradeTarget] = useState(null);

    const loadData = async () => {
        setLoading(true);
        try {
            const [hwRes, subRes] = await Promise.all([
                homeworkService.getHomeworkById(homeworkId),
                homeworkService.getSubmissionsByHomework(homeworkId),
            ]);
            setHomework(extractData(hwRes));
            setEntries(extractList(subRes));
        } catch (err) {
            setEntries([]);
            toast.error(err.response?.data?.message || "Failed to load submissions");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [homeworkId]); // eslint-disable-line

    if (loading) return <div className="page-loading">Loading submissions...</div>;

    const submittedCount = entries.filter((e) => e.status !== "Pending").length;
    const gradedCount = entries.filter((e) => e.status === "Graded").length;

    return (
        <div className="page-container">
            <div className="page-header">
                <button className="btn btn-outline" onClick={() => navigate("/homework")}>
                    <FiArrowLeft /> Back
                </button>
                <h1>{homework?.title} — Submissions</h1>
            </div>

            <div className="stat-strip">
                <div className="stat-chip">
                    <span className="stat-label">Total Students</span>
                    <span className="stat-value">{entries.length}</span>
                </div>
                <div className="stat-chip">
                    <span className="stat-label">Submitted</span>
                    <span className="stat-value">{submittedCount}</span>
                </div>
                <div className="stat-chip">
                    <span className="stat-label">Graded</span>
                    <span className="stat-value">{gradedCount}</span>
                </div>
                <div className="stat-chip">
                    <span className="stat-label">Not Submitted</span>
                    <span className="stat-value">{entries.length - submittedCount}</span>
                </div>
            </div>

            <table className="data-table">
                <thead>
                    <tr>
                        <th>Student</th>
                        <th>Status</th>
                        <th>Submitted On</th>
                        <th>Attachments</th>
                        <th>Marks</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {entries.map((entry) => (
                        <tr key={entry.student?._id || entry.submission?._id}>
                            <td>
                                {getStudentName(entry.student)}
                                {getStudentMeta(entry.student) ? ` (${getStudentMeta(entry.student)})` : ""}
                            </td>
                            <td>
                                <span className={`badge ${statusBadge[entry.status]}`}>{entry.status}</span>
                            </td>
                            <td>
                                {entry.submission?.submittedAt
                                    ? new Date(entry.submission.submittedAt).toLocaleString()
                                    : "—"}
                            </td>
                            <td>
                                {entry.submission?.attachments?.length ? (
                                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        <FiFile /> {entry.submission.attachments.length}
                                    </span>
                                ) : (
                                    "—"
                                )}
                            </td>
                            <td>
                                {entry.submission?.marksObtained !== null &&
                                    entry.submission?.marksObtained !== undefined
                                    ? `${entry.submission.marksObtained}${homework?.maxMarks ? `/${homework.maxMarks}` : ""}`
                                    : "—"}
                            </td>
                            <td>
                                {entry.status === "Pending" ? (
                                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                        Not submitted
                                    </span>
                                ) : (
                                    <button
                                        className="btn btn-outline btn-sm"
                                        onClick={() => setGradeTarget(entry)}
                                    >
                                        {entry.status === "Graded" ? (
                                            <>
                                                <FiCheckCircle /> Regrade
                                            </>
                                        ) : (
                                            <>
                                                <FiEdit3 /> Grade
                                            </>
                                        )}
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {gradeTarget && homework && (
                <GradeModal
                    homework={homework}
                    entry={gradeTarget}
                    onClose={() => setGradeTarget(null)}
                    onSuccess={() => {
                        setGradeTarget(null);
                        loadData();
                    }}
                />
            )}
        </div>
    );
}
